/**
 * Regras de mídia e de container do banner, num só lugar.
 *
 * O app exibe o banner em dois contextos, e o preview reproduz os dois:
 *
 * - **Card do dashboard** — caixa fixa de **320 × 360** (`20rem × 22.5rem`),
 *   proporção **8:9** retrato, com `object-fit: cover`: a mídia é recortada para
 *   preencher a caixa sem distorcer, então as bordas podem sumir.
 * - **Modal "Novidades"** — ocupa o espaço disponível (até `100svh - 8rem`)
 *   mantendo 8:9 em telas menores e crescendo livre nas grandes, com
 *   `object-fit: contain`: a mídia aparece inteira, e sobra tarja quando a
 *   proporção do arquivo não é exatamente 8:9.
 *
 * Consequência para quem produz a arte: **8:9 é o único formato comum aos dois**,
 * e o conteúdo essencial (texto, logo, rosto) precisa ficar na zona central —
 * longe das bordas, que o card recorta.
 *
 * Os valores em `rem` existem porque é neles que os estilos são escritos: px cru
 * não acompanha a preferência de tamanho de fonte do sistema.
 */
export const BANNER_PREVIEW = {
  WIDTH: 320,
  HEIGHT: 360,
  WIDTH_REM: '20rem',
  HEIGHT_REM: '22.5rem',
  /** Proporção do container, no formato aceito por `aspect-ratio`. */
  ASPECT: '8 / 9',
  /** Mesma proporção como rótulo, para os textos de tela. */
  RATIO: '8:9',
  /** Teto de altura do modal: o resto da viewport, descontado o cromo do diálogo. */
  MODAL_MAX_HEIGHT: 'calc(100svh - 8rem)',
} as const;

/**
 * Valores de interpolação dos textos que descrevem o container (i18n).
 *
 * As medidas moram só aqui: as strings de tradução usam `{{width}}`,
 * `{{height}}` e `{{ratio}}`, então mudar o container é mudar
 * {@link BANNER_PREVIEW} — nenhuma tradução precisa ser reescrita.
 */
export const BANNER_PREVIEW_LABEL_VALUES = {
  width: BANNER_PREVIEW.WIDTH,
  height: BANNER_PREVIEW.HEIGHT,
  ratio: BANNER_PREVIEW.RATIO,
} as const;

/** Arte do banner: única resolução aceita (a maior foi descontinuada). */
export const BANNER_IMAGE = {
  WIDTH: 350,
  HEIGHT: 270,
  MAX_BYTES: 10 * 1024 * 1024,
  ACCEPT: 'image/jpeg,image/png',
} as const;

/** Vídeo do banner: MP4/H.264, 3:4 vertical. */
export const BANNER_VIDEO = {
  WIDTH: 1080,
  HEIGHT: 1440,
  MAX_BYTES: 20 * 1024 * 1024,
  ACCEPT: 'video/mp4',
} as const;

/** Motivo de recusa de um arquivo escolhido pelo operador. */
export type BannerFileRejection = 'type' | 'size';

/** Metadados lidos localmente de um MP4 escolhido. */
export interface BannerVideoMetadata {
  readonly file: File;
  /** Duração em segundos, arredondada para o inteiro mais próximo. */
  readonly durationSeconds: number;
  readonly width: number;
  readonly height: number;
}

/** Resultado da leitura de um MP4: metadados ou o motivo da recusa. */
export type BannerVideoReadResult =
  | { readonly ok: true; readonly metadata: BannerVideoMetadata }
  | { readonly ok: false; readonly rejection: BannerFileRejection };

/**
 * Valida uma imagem escolhida para o banner (tipo e tamanho).
 *
 * @param file - Arquivo escolhido pelo operador.
 * @returns `null` quando o arquivo é aceito, ou o motivo da recusa.
 */
export function validateBannerImage(file: File): BannerFileRejection | null {
  if (!BANNER_IMAGE.ACCEPT.split(',').includes(file.type)) return 'type';
  if (file.size > BANNER_IMAGE.MAX_BYTES) return 'size';
  return null;
}

/**
 * Lê duração e resolução de um MP4 **no browser**, sem enviar nada ao servidor.
 *
 * O arquivo é apontado por `URL.createObjectURL` para um `<video>` fora da
 * árvore e os metadados vêm de `loadedmetadata`; a URL é revogada em seguida.
 * H.264 e proporção não são verificados: só tipo e tamanho barram o arquivo.
 *
 * @param file - MP4 escolhido pelo operador.
 * @returns Metadados do vídeo, ou o motivo da recusa.
 *
 * @example
 * ```ts
 * const result = await readBannerVideoMetadata(file);
 * if (result.ok) setValue('duration', result.metadata.durationSeconds);
 * ```
 */
export async function readBannerVideoMetadata(file: File): Promise<BannerVideoReadResult> {
  if (file.type !== BANNER_VIDEO.ACCEPT) return { ok: false, rejection: 'type' };
  if (file.size > BANNER_VIDEO.MAX_BYTES) return { ok: false, rejection: 'size' };

  const url = URL.createObjectURL(file);
  try {
    const metadata = await new Promise<{ duration: number; width: number; height: number }>(
      (resolve, reject) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () =>
          resolve({
            duration: video.duration,
            width: video.videoWidth,
            height: video.videoHeight,
          });
        video.onerror = () => reject(new Error('metadata'));
        video.src = url;
      },
    );

    return {
      ok: true,
      metadata: {
        file,
        durationSeconds: Number.isFinite(metadata.duration) ? Math.round(metadata.duration) : 0,
        width: metadata.width,
        height: metadata.height,
      },
    };
  } catch {
    return { ok: false, rejection: 'type' };
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Formata bytes como `8,4 MB` (pt-BR), para o cartão do arquivo. */
export function formatFileSize(bytes: number): string {
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** Formata segundos como `mm:ss`, para duração e tempo corrente do preview. */
export function formatDurationLabel(totalSeconds: number): string {
  const safe = Number.isFinite(totalSeconds) && totalSeconds > 0 ? Math.floor(totalSeconds) : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

/** Extrai o formato legível (`PNG`, `JPG`, `MP4`) de um MIME type ou nome de arquivo. */
export function formatMediaFormat(mimeTypeOrName: string | undefined): string | undefined {
  if (!mimeTypeOrName) return undefined;
  const fromMime = mimeTypeOrName.match(/^[a-z]+\/([a-z0-9.+-]+)$/i)?.[1];
  const fromName = mimeTypeOrName.split('?')[0]?.split('.').pop();
  const raw = fromMime ?? fromName;
  if (!raw || raw.length > 5) return undefined;
  return raw.toUpperCase().replace('JPEG', 'JPG');
}

/**
 * Descreve a arte escolhida com os dados **reais** do arquivo: resolução, tamanho
 * e formato, na ordem em que estiverem disponíveis.
 *
 * A resolução vem do `naturalWidth`/`naturalHeight` da imagem já carregada (só o
 * browser sabe); tamanho e formato vêm do `File` quando o operador acabou de
 * escolher a arte. Numa arte que já estava no servidor não há `File`, então o
 * formato é inferido da extensão da URL e o tamanho fica de fora.
 *
 * @returns String descritiva, ou `undefined` quando nada é conhecido ainda.
 *
 * @example
 * ```ts
 * describeImageDetails({ width: 350, height: 270, file }); // '350 × 270 · 240 KB · PNG'
 * ```
 */
export function describeImageDetails(params: {
  readonly width?: number | undefined;
  readonly height?: number | undefined;
  readonly file?: File | null | undefined;
  readonly url?: string | undefined;
}): string | undefined {
  const parts: string[] = [];

  if (params.width && params.height) parts.push(`${params.width} × ${params.height}`);
  if (params.file) parts.push(formatFileSize(params.file.size));

  const format = formatMediaFormat(params.file?.type ?? params.url);
  if (format) parts.push(format);

  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/** Formata um limite de bytes como `1 MB` / `20 MB`, para os textos de regra. */
export function formatByteLimit(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/**
 * Valores de interpolação dos textos de regra da **imagem** (i18n).
 *
 * O limite mora em {@link BANNER_IMAGE}; a tradução só diz onde ele entra.
 */
export const BANNER_IMAGE_LABEL_VALUES = {
  maxSize: formatByteLimit(BANNER_IMAGE.MAX_BYTES),
} as const;

/**
 * Valores de interpolação dos textos de regra do **vídeo** (i18n): limite de
 * tamanho, resolução esperada e a altura do container que exibe o banner.
 */
export const BANNER_VIDEO_LABEL_VALUES = {
  maxSize: formatByteLimit(BANNER_VIDEO.MAX_BYTES),
  width: BANNER_VIDEO.WIDTH,
  height: BANNER_VIDEO.HEIGHT,
  containerHeight: BANNER_PREVIEW.HEIGHT,
} as const;

/** Extensões de vídeo que o cadastro pode devolver no caminho da mídia. */
const VIDEO_EXTENSIONS = ['mp4', 'webm', 'mov', 'm4v'] as const;

/**
 * Diz se a mídia salva no banner é um vídeo, olhando a extensão do arquivo.
 *
 * O cadastro devolve **um único caminho** por par (mobile/desktop) para imagem e vídeo, sem
 * dizer qual é qual — então a extensão é o que sobra para diferenciar. Sem isso o
 * banner de vídeo abria em modo imagem e a arte quebrava num `<img>`.
 *
 * @param url - Caminho devolvido pela API.
 *
 * @example
 * ```ts
 * isVideoUrl('https://cdn/banner.mp4'); // true
 * ```
 */
export function isVideoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const extension = url.split('?')[0]?.split('.').pop()?.toLowerCase();
  return extension !== undefined && VIDEO_EXTENSIONS.includes(extension as 'mp4');
}

/** Nome do arquivo extraído de uma URL, para exibir a mídia já cadastrada. */
export function fileNameFromUrl(url: string | null | undefined): string | undefined {
  if (!url) return undefined;
  const name = url.split('?')[0]?.split('/').pop();
  return name && name.length > 0 ? decodeURIComponent(name) : undefined;
}
